<template>
	<section id="classPage" class="class-page" style="padding:15px;">
		<class-page-header :details="details" :current-section="section" :course="course" :menu="menu" :course-content="courseContent"></class-page-header>
		<div class="grades" v-if="grades.length">
			<h2>
				{{ $t('MAIN_CONTENT.classPage.index.classPage_currentGrades') }}
				<font-awesome-icon icon="question-circle"
													size="lg"
													fixed-width
													v-tooltip.top-center="{content: gradesFootNote, trigger: 'hover click'}">
				</font-awesome-icon>
			</h2>
			<ul>
				<li v-for="grade in grades" :key="grade.MP_CODE">
					<a href="#" @click.prevent="goToGradesBreakdown(grade.MP_CODE)">
						{{grade.MP_CODE}}
					</a> <span class="sr-only">:</span> {{grade.GRADE}}
					<br/>
					<span v-if="grade.GRADE_COMPLETED_FLG === 'Y'" v-tooltip.top-center="{content: gradesFootNote, trigger: 'hover click'}">
						**
					</span>
					<span v-else>{{grade.LAST_CALCULATED_DATE}}</span>
				</li>
			</ul>
		</div>
		<div class="class-assignments" v-if="assignments.length > 0">
			<h2>{{ $t('MAIN_CONTENT.commonContent.classPage_upcomingAssignments') }}</h2>
			<span>
				{{ $t('MAIN_CONTENT.commonContent.classPage_upcomingAssignmentTotal', { count: assignments.length }) }}
				<template v-if="dueSoon.length"> | </template> 
				<span class="text-danger"
							v-if="dueSoon.length"
							v-tooltip.top-center="{content: $t('commonText.dueSoonTextDescription'), trigger: 'hover click'}">
					{{ $t('MAIN_CONTENT.AssignmentsCarousel.classPage_dueSoon', { count: dueSoon.length }) }}
				</span>
			</span>
			<div class="view-options">
				<button class="btn btn-transparent"
								:class="{ 'active' : assignmentsView === 'slider' }" 
								type="button"
								@click="setAssignmentsView( 'slider' )">
					<i class="fas fa-columns" aria-hidden="true"></i>
					<span class="sr-only">{{ $t('MAIN_CONTENT.classPage.index.classPage_sliderView') }}</span>
				</button>
				<button class="btn btn-transparent"
								:class="{ 'active' : assignmentsView === 'grid' }"
								type="button"
								@click="setAssignmentsView( 'grid' )">
					<i class="fas fa-th" aria-hidden="true"></i>
					<span class="sr-only">{{ $t('MAIN_CONTENT.classPage.index.classPage_gridView') }}</span>
				</button>
				<button class="btn btn-transparent"
								:class="{ 'active' : assignmentsView === 'list' }"
								type="button"
								@click="setAssignmentsView( 'list' )">
					<i class="fas fa-th-list" aria-hidden="true"></i>
					<span class="sr-only">{{ $t('MAIN_CONTENT.classPage.index.classPage_listView') }}</span>
				</button>
			</div>
			<!-- assignments in slider view -->
			<assignments-carousel :carousel-class="`assignments-carousel color-status ${getDisplayStyle}`"
														:carousel-data="assignments"
														carousel-id="ac-cwp-01"
														color-variant="blank"
														:margin="10"
														:show-nav="true"
														:show-course="false"
														v-if="assignments.length && assignmentsView === 'slider'">
			</assignments-carousel>

			<!-- assignments in grid view -->
			<assignments-grid grid-class="assignments-grid color-status" 
												:grid-data="assignments"
												grid-id="ag-cwp-01" 
												:show-course="false"
												v-else-if="assignments.length && assignmentsView === 'grid'">
			</assignments-grid>

			<!-- assignments in list view -->
			<assignments-list list-class="assignments-list color-status"
												:list-data="assignments"
												list-id="al-cwp-01"
												:show-course="false"
												v-else-if="assignments.length && assignmentsView === 'list'">
			</assignments-list>
		</div>

		<panel-basic :title="`${$t('MAIN_CONTENT.classPage.index.classPage_reportsForStudent', { student: selectedStudent.FIRST_NAME } )}`"
									:id="'reports'"
									:collapsable="true"
									v-if="studentReports.length > 0 || groupReports.length > 0">
			<template slot="title">
				{{ $t('MAIN_CONTENT.classPage.index.classPage_reportsForStudent', { student: selectedStudent.FIRST_NAME } ) }}
			</template>
			<class-items :items-data="studentReports" folder-type="S"></class-items>
			<template v-for="group in groupReports">
				<div :key="group.FOLDER_ID">
					<h3 class="class-items-heading">{{ group.FOLDER_NAME }}</h3>
					<class-items :items-data="group.REPORTS" folder-type="G" :groupId="group.FOLDER_ID"></class-items>
				</div>
			</template>
		</panel-basic>

		<panel-basic :title="`${$t('MAIN_CONTENT.classPage.index.classPage_courseInfo')}`"
									:id="'course-info'"
									:collapsable="true"
									v-if="courseReports.length || courseContent.FOLDER_CONTENT !== ''">
			<template slot="title">
				{{ $t('MAIN_CONTENT.classPage.index.classPage_courseInfo') }}
			</template>
			<div v-html="courseContent.FOLDER_CONTENT"></div>
			<class-items :items-data="courseReports" folder-type="C"></class-items>
		</panel-basic>

		<panel-basic :title="`${$t('MAIN_CONTENT.classPage.index.classPage_courseSections')}`"
									:id="'course-sections'"
									:collapsable="true"
									v-if="menu.length > 0">
			<template slot="title">
				{{ $t('MAIN_CONTENT.classPage.index.classPage_courseSections') }}
			</template>
			<nav aria-label="Course Sections Navigation">
				<class-nav size="lg" :menu="menu" :course="course" :current-section="section" :show-home-section="false" :course-content="courseContent">
				</class-nav>
			</nav>
		</panel-basic>

		<class-page-footer v-if="menu.length > 0" :current-section="section" :course="course" :menu="menu" :course-content="courseContent" />
	</section>
</template>

<script>
	import { mapState, mapGetters, mapActions } from 'vuex';
	import moment from "moment";

	import PanelBasic from 'COMMONS/panels/PanelBasic.vue';

	import ClassPageHeader from "./Header.vue";
	import ClassPageFooter from "./Footer.vue";
	import ClassItems from "./ClassItems.vue";
	import ClassNav from "./ClassNav.vue";
	import AssignmentsCarousel from "./AssignmentsOwlCarousel.vue";
	import AssignmentsGrid from "./AssignmentsGrid.vue";
	import AssignmentsList from "./AssignmentsList.vue";
	import ResultsList from "./ResultsList.vue";

	import ClassPageHomeResource from '../../api/ClassPageHome';
	import ClassPageMenuResource from '../../api/ClassPageMenu';
	import CoursesResource from '../../api/Courses';
	import ClassPageDetailsResource from '../../api/ClassPageDetails';

	export default {
		props: {
			section: {
				type: Number,
				default: 0
			}
		},
		data() {
			return {
				assignmentsView: 'slider',
				grades: [],
				course: {},
				menu: [],
				details: {},
				assignments: [],
				studentReports: [],
				groupReports: [],
				courseReports: [],
				courseContent: {}
			};
		},
		computed: {
			...mapGetters(['selectedStudent', 'roleRules']),
			dueSoon: function(){
				return this.assignments.filter( a => moment( a.END_DATE ).isSameOrBefore( moment().add( 2, 'day' ) ) );
			},
			getDisplayStyle() {
				return this.assignments.length > 3 ? 'displayBlock' : 'displayGrid';
			},
			gradesFootNote() {
				return this.$t( 'MAIN_CONTENT.classPage.index.classPage_gradesFootNote' );
			}
		},
		components: {
			ClassPageHeader,
			ClassPageFooter,
			ClassItems,
			ClassNav,
			AssignmentsCarousel,
			AssignmentsGrid,
			AssignmentsList,
			PanelBasic
		},
		methods: {
			setAssignmentsView( view ) {
				this.assignmentsView = view;
			},
			goToGradesBreakdown(mpCode) {
				this.$router.push({
					name: "ClassPageGradesBreakdown",
					params: {
						courseSectionGUID: this.$route.params.courseSectionGUID,
					},
					query: {
						studentRid: this.selectedStudent.STUDENT_RID,
						mpCode: mpCode,
					}
				});
			}
		},
		created() {
			this.$store.commit('setUseHeader', false);
			this.$nextTick(()=>{
				if(!this.$route.params.courseSectionGUID){
					location.href="/CommunityWebPortal/_app/#/courses-grades";
				}
				const ClassPageHome = new ClassPageHomeResource({
					routeParams: {
						courseSectionGuid: this.$route.params.courseSectionGUID,
					},
					query: {
						student_rid: this.selectedStudent.STUDENT_RID,
					},
				});

				ClassPageHome.get().then(()=>{
					this.grades = ClassPageHome.getResponseData().GRADES;
					this.assignments = ClassPageHome.getResponseData().UPCOMING_ASSIGNMENTS;
					this.studentReports = ClassPageHome.getResponseData().STUDENT_REPORTS;
					this.groupReports = ClassPageHome.getResponseData().STUDENT_GROUP_FOLDERS;
					this.courseReports = ClassPageHome.getResponseData().COURSE_REPORTS;
					this.courseContent = ClassPageHome.getResponseData().COURSE_CONTENT;

					const ClassPageDetails = new ClassPageDetailsResource({
						routeParams: {
							courseSectionGuid: this.$route.params.courseSectionGUID,
						},
						query: {
							student_rid: this.selectedStudent.STUDENT_RID,
						},
					});

					ClassPageDetails.get().then(() => {
						this.details = ClassPageDetails.getResponseData();
							const ClassPageMenu = new ClassPageMenuResource({
							routeParams: {
								courseSectionGuid: this.$route.params.courseSectionGUID,
							},
							query: {
								student_rid: this.selectedStudent.STUDENT_RID,
							},
						});

						ClassPageMenu.get().then(()=>{
							this.menu = ClassPageMenu.getResponseData();
							const Courses = new CoursesResource({
								routeParams: {
									courseSectionGuid: this.$route.params.courseSectionGUID,
								},
								query: {
									student_rid: this.selectedStudent.STUDENT_RID,
								},
							});

							Courses.get().then(()=>{
								this.course = Courses.getResponseData();
							});
						});
					});
				});
			})
			
		}
	}
</script>
